import React, { useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextInput from "../../components/TextInput/TextInput.js";
import DocumentController from "./Controller/document_controller.ts";
import BroadcastController from './Controller/broadcast_controller.ts';
import { notification } from "antd";
import FileInput from "../../components/FileInput/FileInput.js";
import SelectInput from "../../components/TextInput/SelectInput";
import MultipleSelectChip from "../../components/TextInput/MultipleSelectChip";
import InfinityEncryptor from "../../components/EncryptDecrypt/InfinityEncryptor.js";
import CustomSeparator from "./../../layouts/AdminLayout/Header/Breadcrumbs.js";
import BasicDateTimePicker from "../../components/DatePicker/BasicDateTimePicker.js";
import { MenuItem, Select } from "@mui/material";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Dashboard = () => {
  const fileInputRef = useRef(null); // Create a ref for the file input
  const [clearFileInput, setClearFileInput] = useState(false);
  const [clearSelectMultipleInput, setClearSelectMultipleInput] =
    useState(false);

  const userID = localStorage.getItem("userID");

  // notification
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, desc, msg) => {
    api[type]({
      message: msg,
      description: desc,
    });
  };
  // encryptor
  const key = "12345";
  const mode = "YMDHI";
  const Encryptor = new InfinityEncryptor(key, mode);

  const required = () => (
    <span style={{ color: "red", marginLeft: "5px", fontSize: "17px" }}>*</span>
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      userID: userID,
      title: "",
      message: "",
      poster: "",
      startDateTime: "",
      endDateTime: "",
      documentAccess: "",
      documentAccessAccountType: "",
      documentAccessCommitteeType: "",
      publish_status: "1"
    },
  });

  const onSubmit = async (data) => {
    setClearSelectMultipleInput(false);

    const formData = new FormData();
    formData.append("userID", data.userID);
    formData.append("title", Encryptor.Encrypt(data.title));
    formData.append("message", Encryptor.Encrypt(data.message));

    if (data.poster && data.poster.length > 0) {
      formData.append("file", data.poster[0]);
    }
    formData.append(
      "startDateTime",
      Encryptor.Encrypt(data.startDateTime)
    );
    formData.append(
        "endDateTime",
        Encryptor.Encrypt(data.endDateTime)
      );
    formData.append(
      "viewable_member_typeIDs",
      Encryptor.Encrypt(data.resourcesMembershipAccess)
    );
    formData.append(
      "viewable_member_user_types",
      Encryptor.Encrypt(data.resourcesAccessAccountType)
    );
    formData.append(
      "viewable_committee_types",
      Encryptor.Encrypt(data.resourcesAccessCommitteeType)
    );

    formData.append("publish_status", Encryptor.Encrypt(data.publish_status));


    if (Object.keys(errors).length > 0) {
      console.log("Form has errors:", errors);
      return; // Prevent submission if there are errors
    }
    try {
      BroadcastController.createBroadcast(formData).then((response) => {
        console.log(response);
        if (response?.status === "success") {
          reset();
          setClearFileInput((prev) => !prev);
          setClearSelectMultipleInput(true);
          if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Clear the file input field
          }
          openNotificationWithIcon(
            "success",
            "Your data has been saved successfully!",
            "Success"
          );
        }
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
    // console.log(data.title)
    // console.log(data.message)
    // console.log(data.poster)
    // console.log(data.startDateTime)
    // console.log(data.endDateTime)
    // console.log(data.documentAccess)
    // console.log(data.documentAccessAccountType)
    // console.log(data.documentAccessCommitteeType)
  };

  const onInvalid = (errors) => {
    console.log("Form has errors:", errors);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "#e8e8e8",
        height: "100%",
        width: "100vw",
      }}
    >
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <CustomSeparator pageName="create_broadcast" />
        {contextHolder}

        <Typography gutterBottom variant="h4" sx={{ color: "text.secondary" }}>
          Create Broadcast
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid, {
            shouldFocusError: true,
          })}
        >
          {/* title */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Title{required()}</label>
            </Grid>
            <Grid size={8}>
              <TextInput
                type="text"
                {...register("title", {
                  required: "This field is required",
                })}
                onErrorCheck={errors.label ? errors.label.message : ""}
                control={control}
              />
            </Grid>
          </Grid>
          {/* message */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Message{required()}</label>
            </Grid>
            <Grid size={8}>
              <TextInput
                type="text"
                {...register("message", {
                  required: "This field is required",
                })}
                onErrorCheck={
                  errors.description ? errors.description.message : ""
                }
                control={control}
              />
            </Grid>
          </Grid>

          {/* poster */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Choose Poster{required()}</label>
            </Grid>
            <Grid size={8}>
              <FileInput
                label="Upload Poster"
                name="poster"
                control={control}
                rules={{ required: "This field is required" }}
                helperText={errors.poster && errors.poster.message}
                inputRef={fileInputRef} // Assign the ref to the FileInput component
                onClearInput={clearFileInput}
              />
            </Grid>
          </Grid>

          {/* start datetime and end datetime */}
          <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid size={4}>
                <label>Start Date and Time{required()}</label>
              </Grid>
              <Grid size={8}>
                <BasicDateTimePicker
                  {...register("startDateTime", {
                    required: "This field is required",
                  })}
                  onErrorCheck={
                    errors.documentExpiredDateTime
                      ? errors.documentExpiredDateTime.message
                      : ""
                  }
                  control={control}
                />
              </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid size={4}>
                <label>End Date and Time{required()}</label>
              </Grid>
              <Grid size={8}>
                <BasicDateTimePicker
                  {...register("endDateTime", {
                    required: "This field is required",
                  })}
                  onErrorCheck={
                    errors.documentExpiredDateTime
                      ? errors.documentExpiredDateTime.message
                      : ""
                  }
                  control={control}
                />
              </Grid>
          </Grid>

          {/* membership access */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Membership Access</label>
            </Grid>
            <Grid size={8}>
              <MultipleSelectChip
                type="select"
                {...register("resourcesMembershipAccess", {
                  required: false,
                })}
                onErrorCheck={
                  errors.resourcesMembershipAccess ? errors.resourcesMembershipAccess.message : ""
                }
                control={control}
                clear={clearSelectMultipleInput}
                setValue={setValue} // Pass setValue to the child component
              />
            </Grid>
          </Grid>

          {/* account access */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Account Access</label>
            </Grid>
            <Grid size={8}>
              <MultipleSelectChip
                type="select"
                {...register("resourcesAccessAccountType", {
                  required: false,
                })}
                onErrorCheck={
                  errors.resourcesAccessAccountType
                    ? errors.resourcesAccessAccountType.message
                    : ""
                }
                control={control}
                clear={clearSelectMultipleInput}
                setValue={setValue} // Pass setValue to the child component
              />
            </Grid>
          </Grid>
          
          {/* committee access */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Committee Access</label>
            </Grid>
            <Grid size={8}>
              <MultipleSelectChip
                type="select"
                {...register("resourcesAccessCommitteeType", {
                  required: false,
                })}
                onErrorCheck={
                  errors.resourcesAccessCommitteeType
                    ? errors.resourcesAccessCommitteeType.message
                    : ""
                }
                control={control}
                clear={clearSelectMultipleInput}
                setValue={setValue} // Pass setValue to the child component
              />
            </Grid>
          </Grid>

            {/* committee access */}
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={4}>
                <label>Publish Status</label>
                </Grid>
                <Grid size={8}>
                <SelectInput
                    type="select"
                    {...register("publish_status", {
                    required: false,
                    })}
                    onErrorCheck={
                    errors.publish_status
                        ? errors.publish_status.message
                        : ""
                    }
                    control={control}
                    setValue={setValue} // Pass setValue to the child component
                />
                </Grid>
            </Grid>

          <Box textAlign="center" sx={{ paddingBottom: "80px" }}>
            <Button
              sx={{
                borderRadius: "20px",
                mt: 4,
                mb: 4,
                // width: "93.4%",
                height: "48px",
                backgroundColor: "#d33333",
                position: "fixed", // Makes the button fixed
                bottom: "16px", // Position the button 16px from the bottom
                transform: "translateX(-50%)", // Aligns the button in the center
                zIndex: 1000, // Ensures the button stays on top of other elements
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)", // Floating shadow
                transition: "all 0.3s ease", // Smooth transition for hover effects

                "&:hover": {
                  backgroundColor: "#b02e2e", // Darker red on hover
                  boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.3)", // Larger shadow on hover
                  transform: "translate(-50%, -5px)", // Moves up slightly on hover
                },
              }}
              variant="contained"
              type="submit"
            >
              <Typography variant="h6">Save</Typography>
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default Dashboard;
